import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Action,
  IntegrationAppClient,
  useIntegrationApp,
} from '@integration-app/react';
import { IntegrationAppActionKey } from 'src/services/integration-app.service';

type ProviderOption = { label: string; value: string };
type Providers = { email: ProviderOption[]; sms: ProviderOption[] };

type IntegrationAppProvidersContextType = {
  emailProviders: ProviderOption[];
  smsProviders: ProviderOption[];
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
};

export const IntegrationAppProvidersContext =
  createContext<IntegrationAppProvidersContextType | null>(null);

async function loadProviders(
  integrationApp: IntegrationAppClient,
): Promise<Providers> {
  const actions = await integrationApp?.actions.findAll();

  const emailAction = actions?.find(
    (a) => a.key === IntegrationAppActionKey.SEND_EMAIL,
  );
  const smsAction = actions?.find(
    (a) => a.key === IntegrationAppActionKey.SEND_MESSAGE,
  );

  const [emailFull, smsFull] = await Promise.all([
    emailAction ? integrationApp?.action(emailAction.id).get() : null,
    smsAction ? integrationApp?.action(smsAction.id).get() : null,
  ]);

  const map = (action: Action): ProviderOption[] =>
    (action?.appliedToIntegrations ?? []).map((item) => ({
      label: item.integration.name,
      value: item.integration.key,
    }));

  return {
    email: emailFull ? map(emailFull) : [],
    sms: smsFull ? map(smsFull) : [],
  };
}

export function IntegrationAppProvidersProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const integrationApp = useIntegrationApp();

  const [data, setData] = useState<Providers | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);

  // Keep the latest in-flight promise and a soft-abort flag inside this provider instance.
  const inflightRef = useRef<Promise<Providers> | null>(null);
  const abortRef = useRef<{ aborted: boolean }>({ aborted: false });

  const startFetch = async () => {
    if (!integrationApp) return;
    setIsLoading(true);
    setError(null);

    const p = loadProviders(integrationApp);
    inflightRef.current = p;
    // new fetch -> reset abort flag
    abortRef.current = { aborted: false };

    try {
      const result = await p;
      if (!abortRef.current.aborted) {
        setData(result);
        setIsLoading(false);
      }
    } catch (e) {
      if (!abortRef.current.aborted) {
        setError(e);
        setIsLoading(false);
      }
    } finally {
      if (inflightRef.current === p) inflightRef.current = null;
    }
  };

  useEffect(() => {
    if (!integrationApp) return;

    // whenever integrationApp reference changes, reset & fetch
    setData(null);
    setIsLoading(true);
    setError(null);
    startFetch();

    return () => {
      // soft-cancel the last request on unmount or when integrationApp changes
      abortRef.current.aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationApp]);

  const refetch = async () => {
    await startFetch();
  };

  const value = useMemo<IntegrationAppProvidersContextType>(
    () => ({
      emailProviders: data?.email ?? [],
      smsProviders: data?.sms ?? [],
      isLoading,
      error,
      refetch,
    }),
    [data, isLoading, error],
  );

  return (
    <IntegrationAppProvidersContext.Provider value={value}>
      {children}
    </IntegrationAppProvidersContext.Provider>
  );
}
